import {
  type BoardSummary,
  CARD_DEFAULTS,
  type CardType,
  can,
  createCard,
  getBoardTypes,
  LOCAL,
  type Role,
} from "@mn/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useSession } from "@/app/session";
import { ActivityPanel, Bell } from "@/board/ActivityPanel";
import { Actions } from "@/board/actions";
import { BoardContext, type BoardCtx } from "@/board/BoardContext";
import { Canvas } from "@/board/Canvas";
import { ContextMenu } from "@/board/ContextMenu";
import { zoomAt, zoomToBounds } from "@/board/camera";
import { markTileHintDone } from "@/board/cards/BoardCard";
import { type BoardHandle, openBoard, presenceColor } from "@/board/collab";
import { imageDimensions, localPreviews, setUploadProgress, uploadFile } from "@/board/files";
import { useFlaggedCards, useMapKeys } from "@/board/hooks";
import { Interactions } from "@/board/interactions";
import { useLabels } from "@/board/labels";
import { Pickers } from "@/board/Pickers";
import { PresenceAvatars } from "@/board/Presence";
import { Presentation } from "@/board/Presentation";
import { QuickNotesPanel } from "@/board/QuickNotesPanel";
import { ShortcutsDialog } from "@/board/ShortcutsDialog";
import { bindShortcuts } from "@/board/shortcuts";
import { SpatialIndex } from "@/board/spatial";
import { useBoardUI } from "@/board/store";
import { TemplateGallery } from "@/board/TemplateGallery";
import { Toolbar } from "@/board/Toolbar";
import { TrashPanel } from "@/board/TrashPanel";
import { UnsortedPanel } from "@/board/UnsortedPanel";
import { VersionsPanel } from "@/board/VersionsPanel";
import { LanguageToggle } from "@/components/LanguageToggle";
import { FeedbackButton, openFeedback } from "@/components/FeedbackDialog";
import { SearchButton, SearchDialog, useSearchHotkey } from "@/components/SearchDialog";
import { Alert, Button, Spinner, Wordmark } from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { setUsageBoard, trackUsage } from "@/lib/usage";
import { BoardSettingsDialog } from "./BoardSettingsDialog";
import { ExportMenu } from "./ExportMenu";
import { ShareDialog } from "./ShareDialog";

interface BoardView {
  board: BoardSummary;
  role: Role;
  breadcrumbs: { id: string; title: string }[];
  children: BoardSummary[];
  privateVia: { id: string; title: string; ownerName: string | null } | null;
}

export interface ShareInfo {
  boardId: string;
  title: string;
  allowComments: boolean;
  allowDownload: boolean;
  showAuthors: boolean;
}

export default function BoardPage({ share }: { share?: ShareInfo } = {}) {
  const params = useParams();
  const id = share?.boardId ?? params.id ?? "";
  const nav = useNavigate();
  const { me: session } = useSession();
  // Visitors of a published link have no account: give them an anonymous identity.
  const me =
    session ??
    (share
      ? ({
          user: { id: "anon", name: "Invitado", email: "", image: null, emailVerified: false },
          workspace: { id: "", name: "", rootBoardId: null },
          staffRole: null,
        } as NonNullable<typeof session>)
      : null);
  const t = useT();
  const [view, setView] = useState<BoardView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("connecting");
  const [synced, setSynced] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [unsortedOpen, setUnsortedOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [gallery, setGallery] = useState<null | { into?: string }>(null);
  const [offerTemplates, setOfferTemplates] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const viewportHostRef = useRef<HTMLDivElement>(null);
  const [ctx, setCtx] = useState<BoardCtx | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  useSearchHotkey(setSearchOpen);
  const [settingsFor, setSettingsFor] = useState<BoardSummary | null>(null);
  const [presenting, setPresenting] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Duplicate a child board (deep copy on the server), then reload the children list so its tile appears.
  useEffect(() => {
    if (!view) return;
    const onDup = async (e: Event) => {
      const bid = (e as CustomEvent<{ boardId?: string }>).detail?.boardId;
      if (!bid) return;
      try {
        await api(`/api/boards/${bid}/duplicate`, { method: "POST" });
        setView(await api<BoardView>(`/api/boards/${view.board.id}`));
      } catch {
        alert(t.error);
      }
    };
    window.addEventListener("mn:duplicate-board", onDup);
    return () => window.removeEventListener("mn:duplicate-board", onDup);
  }, [view, t]);
  useEffect(() => {
    if (!ctx) return;
    const onOpen = (e: Event) => ctx.openBoard((e as CustomEvent<{ boardId: string }>).detail.boardId);
    window.addEventListener("mn:open-board", onOpen);
    return () => window.removeEventListener("mn:open-board", onOpen);
  }, [ctx]);
  useEffect(() => {
    const onPresent = () => setPresenting((v) => !v);
    const onHelp = () => setHelpOpen((v) => !v);
    window.addEventListener("mn:present", onPresent);
    window.addEventListener("mn:help", onHelp);
    return () => {
      window.removeEventListener("mn:present", onPresent);
      window.removeEventListener("mn:help", onHelp);
    };
  }, []);
  // Board settings for this board (empty detail) or for a child tile (its boardId).
  useEffect(() => {
    const onOpen = async (e: Event) => {
      const bid = (e as CustomEvent<{ boardId?: string }>).detail?.boardId;
      if (!bid) {
        if (view) setSettingsFor(view.board);
        return;
      }
      try {
        setSettingsFor((await api<{ board: BoardSummary }>(`/api/boards/${bid}`)).board);
      } catch {}
    };
    window.addEventListener("mn:board-settings", onOpen);
    return () => window.removeEventListener("mn:board-settings", onOpen);
  }, [view]);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadAccept, setUploadAccept] = useState("image/*");

  // 1. Load board metadata (role, breadcrumbs).
  useEffect(() => {
    setView(null);
    setCtx(null);
    setSynced(false);
    api<BoardView>(`/api/boards/${id}`)
      .then((v) => {
        setView(v);
        setUsageBoard(v.board.id);
        trackUsage("board_open", undefined, v.board.id);
      })
      .catch((e) =>
        setError(
          e instanceof ApiError && e.status === 404
            ? t.boardNotFound
            : e instanceof ApiError && e.code === "private"
              ? t.boardPrivate
              : t.error,
        ),
      );
  }, [id, t]);

  // 2. Open the collaborative document once we know the role.
  useEffect(() => {
    if (!view || !me || !viewportHostRef.current) return;
    const user = {
      id: me.user.id,
      name: me.user.name,
      image: me.user.image,
      color: presenceColor(me.user.id),
    };
    const canEdit = can(view.role, "edit");
    const handle: BoardHandle = openBoard(view.board.id, user, {
      readOnly: !canEdit,
      tokenUrl: share ? "/api/share/collab-token" : undefined,
      onStatus: setStatus,
      onSynced: () => setSynced(true),
      onAuthFailed: () => setError(t.boardNotFound),
    });
    const index = new SpatialIndex(handle.types.cards);
    const interactions = new Interactions(viewportHostRef.current, handle, index, user.id, canEdit);
    useBoardUI.setState({
      camera: { x: 100, y: 100, z: 1 },
      selection: new Set(),
      editingId: null,
      contextMenu: null,
    });
    setCtx({
      boardId: view.board.id,
      handle,
      index,
      interactions,
      role: view.role,
      canEdit,
      user,
      openBoard: (bid) => {
        markTileHintDone();
        nav(share ? `${location.pathname}?b=${bid}` : `/b/${bid}`);
      },
    });
    return () => {
      handle.close();
    };
  }, [view, me, nav, t]);

  useEffect(() => {
    void useLabels.getState().load();
  }, []);

  const actions = useMemo(() => {
    if (!ctx || !me) return null;
    const a = new Actions(ctx.handle, ctx.boardId, me.user.id);
    a.index = ctx.index;
    return a;
  }, [ctx, me]);

  // 3. Make sure every child board (from SQL) has exactly one tile card on this canvas.
  useEffect(() => {
    if (!ctx || !synced || !view || !ctx.canEdit || !ctx.handle.provider.isSynced) return;
    const { cards } = getBoardTypes(ctx.handle.doc);
    const byChild = new Map<string, { id: string; createdAt: number }[]>();
    cards.forEach((c, id) => {
      const child = c.get("childBoardId") as string | undefined;
      if (child)
        byChild.set(child, [
          ...(byChild.get(child) ?? []),
          { id, createdAt: (c.get("createdAt") as number) ?? 0 },
        ]);
    });
    const missing = view.children.filter((b) => !byChild.has(b.id));
    const dupes = [...byChild.values()].flatMap((list) =>
      list
        .sort((a, b) => a.createdAt - b.createdAt)
        .slice(1)
        .map((x) => x.id),
    );
    // Tiles stacked on the exact same spot (older versions put every new tile at 100,100): spread them out.
    const stacked: string[] = [];
    const seenPos = new Set<string>();
    for (const [id, c] of cards.entries()) {
      if (c.get("type") !== "board" || c.get("parent") || c.get("unsorted")) continue;
      const key = `${c.get("x")},${c.get("y")}`;
      if (seenPos.has(key)) stacked.push(id);
      else seenPos.add(key);
    }
    // Tiles whose board was trashed elsewhere (API, another board's trash) are removed; staff only,
    // since the children list is complete only for them.
    const childIds = new Set(view.children.map((b) => b.id));
    const orphans =
      ctx.role === "owner"
        ? [...byChild.entries()]
            .filter(([child]) => !childIds.has(child))
            .flatMap(([, l]) => l.map((x) => x.id))
        : [];
    if (missing.length === 0 && dupes.length === 0 && stacked.length === 0 && orphans.length === 0) return;
    // New tiles go to the first free slot of a grid starting at (100,100), so boards created
    // elsewhere (API, templates, moves) never stack on top of each other.
    const taken = new Set<string>();
    const slotFree = (x: number, y: number) =>
      !taken.has(`${x},${y}`) && ctx.index.inRect({ x: x + 4, y: y + 4, w: 112, h: 152 }).length === 0;
    const nextSlot = () => {
      for (let k = 0; k < 600; k++) {
        const x = 100 + (k % 6) * 150;
        const y = 100 + Math.floor(k / 6) * 180;
        if (slotFree(x, y)) {
          taken.add(`${x},${y}`);
          return { x, y };
        }
      }
      return { x: 100, y: 100 };
    };
    ctx.handle.doc.transact(() => {
      for (const id of dupes) cards.delete(id);
      for (const id of orphans) cards.delete(id);
      for (const id of stacked) {
        const { x, y } = nextSlot();
        cards.get(id)?.set("x", x);
        cards.get(id)?.set("y", y);
      }
      for (const b of missing) {
        const { x, y } = nextSlot();
        createCard(ctx.handle.doc, {
          type: "board",
          x,
          y,
          userId: ctx.user.id,
          fields: { childBoardId: b.id },
        });
      }
    }, "system");
  }, [ctx, synced, view]);

  // 3a. A freshly created, empty sub-board offers the template picker once.
  useEffect(() => {
    if (!ctx || !synced || !view || !ctx.canEdit || share) return;
    const empty = ctx.handle.types.cards.size === 0 && view.children.length === 0 && !!view.board.parentId;
    setOfferTemplates(empty && sessionStorage.getItem(`mn_tpl_offered_${view.board.id}`) !== "1");
  }, [ctx, synced, view, share]);
  useEffect(() => {
    const onConvert = async (e: Event) => {
      const { boardId } = (e as CustomEvent<{ boardId?: string }>).detail;
      if (!boardId) return;
      const name = prompt(t.convertToTemplate, "");
      if (name === null) return;
      await api(`/api/boards/${boardId}/template`, { method: "POST", json: { name: name || undefined } });
      alert(t.templateSaved);
    };
    window.addEventListener("mn:convert-template", onConvert);
    return () => window.removeEventListener("mn:convert-template", onConvert);
  }, [t]);

  // 3b. Deep link: ?card=<id> selects the card and centres it once the doc has synced.
  useEffect(() => {
    if (!ctx || !synced) return;
    const target = new URLSearchParams(location.search).get("card");
    if (!target) return;
    const r = ctx.index.rectOf(target);
    const { w, h } = useBoardUI.getState().viewport;
    if (r)
      useBoardUI
        .getState()
        .setCamera(zoomToBounds({ x: r.x - 200, y: r.y - 200, w: r.w + 400, h: r.h + 400 }, w, h, 40, 1));
    if (ctx.handle.types.cards.has(target)) useBoardUI.getState().select([target]);
  }, [ctx, synced]);

  // 4. Shortcuts and camera helpers.
  const zoomFit = useCallback(() => {
    if (!ctx) return;
    const b = ctx.index.contentBounds();
    const { w, h } = useBoardUI.getState().viewport;
    useBoardUI
      .getState()
      .setCamera(
        b
          ? zoomToBounds({ x: b.x - 40, y: b.y - 40, w: b.w + 80, h: b.h + 80 }, w, h)
          : { x: 100, y: 100, z: 1 },
      );
  }, [ctx]);
  useEffect(() => {
    if (!actions || !ctx) return;
    const center = () => {
      const { w, h } = useBoardUI.getState().viewport;
      return { sx: w / 2, sy: h / 2 };
    };
    const unbind = bindShortcuts(actions, {
      zoomFit,
      zoomIn: () => useBoardUI.getState().setCamera((c) => zoomAt(c, center().sx, center().sy, c.z * 1.2)),
      zoomOut: () => useBoardUI.getState().setCamera((c) => zoomAt(c, center().sx, center().sy, c.z / 1.2)),
      resetZoom: () => useBoardUI.getState().setCamera((c) => zoomAt(c, center().sx, center().sy, 1)),
      setSpace: (v) => {
        ctx.interactions.spaceDown = v;
      },
    });
    const onFit = () => zoomFit();
    window.addEventListener("mn:zoom-fit", onFit);
    return () => {
      unbind();
      window.removeEventListener("mn:zoom-fit", onFit);
    };
  }, [actions, ctx, zoomFit]);

  /** Free spot near the viewport centre: step down-right until the target rectangle overlaps nothing. */
  const freeSpot = useCallback(
    (w: number, h: number) => {
      const { camera, viewport } = useBoardUI.getState();
      let x = viewport.w / 2 / camera.z - camera.x - w / 2;
      let y = viewport.h / 2 / camera.z - camera.y - h / 2;
      for (
        let i = 0;
        i < 12 && ctx && ctx.index.inRect({ x: x + 4, y: y + 4, w: w - 8, h: h - 8 }).length > 0;
        i++
      ) {
        x += 30;
        y += 30;
      }
      return { x, y };
    },
    [ctx],
  );
  const createAtCenter = useCallback(
    (type: CardType) => {
      if (!actions) return;
      const d = CARD_DEFAULTS[type];
      const p = freeSpot(d.w, d.h ?? 80);
      void actions.create(type, p.x, p.y);
    },
    [actions, freeSpot],
  );

  // 5. Uploads: one placeholder card per file (image/video/audio/file), then up to 3 uploads at a time with progress.
  const addImages = useCallback(
    async (files: File[], at?: { x: number; y: number }) => {
      if (!actions || !ctx) return;
      const { camera, viewport } = useBoardUI.getState();
      const base = at ?? {
        x: viewport.w / 2 / camera.z - camera.x - 140,
        y: viewport.h / 2 / camera.z - camera.y - 100,
      };
      const jobs: { cid: string; f: File }[] = [];
      let i = 0;
      for (const f of files) {
        const kind: CardType = f.type.startsWith("image/")
          ? "image"
          : f.type.startsWith("video/")
            ? "video"
            : f.type.startsWith("audio/")
              ? "audio"
              : "file";
        const dims = kind === "image" ? await imageDimensions(f) : null;
        const aspect = dims ? dims.w / dims.h : 4 / 3;
        const w = kind === "image" || kind === "video" ? 280 : 260;
        const cid = await actions.create(kind, base.x + (i % 3) * 300, base.y + Math.floor(i / 3) * 240, {
          w,
          h: kind === "image" ? Math.round(w / aspect) : null,
          aspect,
          name: f.name,
          mime: f.type,
          size: f.size,
          uploading: true,
          fileId: null,
        });
        i++;
        if (kind === "image" || kind === "video" || kind === "audio")
          localPreviews.set(cid, URL.createObjectURL(f));
        jobs.push({ cid, f });
      }
      useBoardUI.getState().clearSelection();
      const run = async ({ cid, f }: { cid: string; f: File }) => {
        try {
          const up = await uploadFile(f, ctx.boardId, (pct) => setUploadProgress(cid, pct));
          ctx.handle.doc.transact(() => {
            const c = ctx.handle.types.cards.get(cid);
            if (!c) return;
            c.set("fileId", up.id);
            c.set("uploading", false);
            c.set("mime", up.mime);
            c.set("size", up.size);
            if (up.width && up.height) c.set("aspect", up.width / up.height);
          }, LOCAL);
        } catch {
          ctx.handle.doc.transact(() => ctx.handle.types.cards.delete(cid), LOCAL);
        } finally {
          setUploadProgress(cid, null);
          const u = localPreviews.get(cid);
          if (u) setTimeout(() => URL.revokeObjectURL(u), 5000);
          localPreviews.delete(cid);
        }
      };
      const queue = [...jobs];
      await Promise.all(
        Array.from({ length: 3 }, async () => {
          for (let j = queue.shift(); j; j = queue.shift()) await run(j);
        }),
      );
    },
    [actions, ctx],
  );

  // Drop / paste of files anywhere on the page.
  useEffect(() => {
    if (!ctx?.canEdit) return;
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer?.files.length) return;
      e.preventDefault();
      const host = viewportHostRef.current!.getBoundingClientRect();
      const { camera } = useBoardUI.getState();
      void addImages([...e.dataTransfer.files], {
        x: (e.clientX - host.left) / camera.z - camera.x,
        y: (e.clientY - host.top) / camera.z - camera.y,
      });
    };
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
    };
    const onPaste = (e: ClipboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      if (el?.isContentEditable || el?.tagName === "INPUT" || el?.tagName === "TEXTAREA") return;
      const files = [...(e.clipboardData?.files ?? [])];
      if (files.length) {
        e.preventDefault();
        void addImages(files);
        return;
      }
      const text = e.clipboardData?.getData("text/plain")?.trim() ?? "";
      if (/^(https?:\/\/|www\.)\S+$/i.test(text) && actions) {
        e.preventDefault();
        const p = freeSpot(280, 120);
        void actions.create("link", p.x, p.y, { url: text }).then((cid) => actions.resolveLink(cid, text));
      } else if (text && actions) {
        e.preventDefault();
        const p = freeSpot(240, 80);
        void actions.createNoteWithText(p.x, p.y, text);
      }
    };
    const onResolve = (e: Event) => {
      const { id, url } = (e as CustomEvent<{ id: string; url: string }>).detail;
      void actions?.resolveLink(id, url);
    };
    const onReaction = (e: Event) => {
      const { id, emoji } = (e as CustomEvent<{ id: string; emoji: string }>).detail;
      actions?.toggleReaction(emoji, [id]);
    };
    const onMove = (e: Event) => {
      const { boardId, ids } = (e as CustomEvent<{ boardId: string; ids: string[] }>).detail;
      void actions?.moveToBoard(boardId, ids);
    };
    window.addEventListener("mn:move-to-board", onMove);
    window.addEventListener("mn:toggle-reaction", onReaction);
    window.addEventListener("mn:resolve-link", onResolve);
    window.addEventListener("drop", onDrop);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("paste", onPaste);
      window.removeEventListener("mn:resolve-link", onResolve);
      window.removeEventListener("mn:toggle-reaction", onReaction);
      window.removeEventListener("mn:move-to-board", onMove);
    };
  }, [ctx, addImages, actions, freeSpot]);

  async function rename(title: string) {
    if (!view || !title.trim()) return setRenaming(false);
    await api(`/api/boards/${view.board.id}`, { method: "PATCH", json: { title: title.trim() } });
    setView({
      ...view,
      board: { ...view.board, title: title.trim() },
      breadcrumbs: view.breadcrumbs.map((c) => (c.id === view.board.id ? { ...c, title: title.trim() } : c)),
    });
    setRenaming(false);
  }

  if (error)
    return (
      <div className="p-8">
        <Alert kind="error">{error}</Alert>
        <Link to="/" className="mt-4 inline-block text-sm">
          {t.back}
        </Link>
      </div>
    );

  return (
    <div className="flex h-full flex-col">
      <header
        className={`z-30 flex h-12 shrink-0 items-center gap-3 border-b border-border bg-panel px-3 ${presenting ? "hidden" : ""}`}
      >
        <Wordmark className="text-lg" />
        <nav className="flex min-w-0 items-center gap-1 text-sm text-muted">
          {view?.breadcrumbs.map((c, i) => (
            <span
              key={c.id}
              className={`flex min-w-0 items-center gap-1 ${i < view.breadcrumbs.length - 1 ? "max-md:hidden" : ""}`}
            >
              {i > 0 ? <span className="text-dim max-md:hidden">›</span> : null}
              {i === view.breadcrumbs.length - 1 ? (
                renaming && ctx?.canEdit ? (
                  <input
                    autoFocus
                    defaultValue={c.title}
                    onBlur={(e) => rename(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") rename((e.target as HTMLInputElement).value);
                      if (e.key === "Escape") setRenaming(false);
                    }}
                    className="rounded-sm border border-border bg-panel-2 px-2 py-0.5 text-text outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onDoubleClick={() => ctx?.canEdit && setRenaming(true)}
                    className="truncate font-semibold text-text"
                    title={t.rename}
                  >
                    {c.title}
                  </button>
                )
              ) : (
                <Link
                  to={share ? `${location.pathname}?b=${c.id}` : `/b/${c.id}`}
                  className="truncate hover:text-text"
                >
                  {c.title}
                </Link>
              )}
            </span>
          ))}
        </nav>
        {view && me?.staffRole && !share ? (
          <VisibilityPill
            view={view}
            onClick={
              ctx?.role === "owner" && view.board.parentId ? () => setSettingsFor(view.board) : undefined
            }
          />
        ) : null}
        <span className="ml-2 text-[11px] text-dim max-md:hidden">
          {!ctx
            ? t.loading
            : !synced
              ? t.syncing
              : ctx.canEdit
                ? (t.status[status] ?? status)
                : t.status.readOnly}
        </span>
        <span className="text-[11px] text-dim max-md:hidden" title={__APP_VERSION__}>
          {__APP_VERSION__.split(" · ")[0]}
        </span>
        <div className="ml-auto flex items-center gap-3 text-sm">
          {ctx ? (
            <BoardContext.Provider value={ctx}>
              <PresenceAvatars />
            </BoardContext.Provider>
          ) : null}
          {session && !share ? <SearchButton /> : null}
          {session && !share ? (
            <button
              type="button"
              onClick={() => setQuickOpen((v) => !v)}
              className="whitespace-nowrap rounded-sm border border-border px-2 py-1 text-xs text-muted hover:text-text"
              title={t.quickNotes}
            >
              ✎ <span className="max-lg:hidden">{t.quickNotes}</span>
            </button>
          ) : null}
          <div className="hidden items-center gap-3 md:flex">
            {ctx && can(ctx.role, "manage") && view?.board.parentId ? (
              <Button
                variant="ghost"
                className="whitespace-nowrap px-3 py-1.5 text-xs"
                onClick={() => setSharing(true)}
                title={t.inviteGuest}
              >
                {t.share}
              </Button>
            ) : null}
            {ctx && view ? <ExportMenu boardId={view.board.id} /> : null}
            {ctx ? (
              <button
                type="button"
                onClick={() => (setPresenting(true), trackUsage("present"))}
                className="whitespace-nowrap rounded-sm border border-border px-2 py-1 text-xs text-muted hover:text-text"
                title={`${t.present} (P)`}
              >
                ▶ {t.present}
              </button>
            ) : null}
            {ctx?.canEdit && view && !share ? (
              <button
                type="button"
                onClick={() => setSettingsFor(view.board)}
                className="rounded-sm border border-border px-2 py-1 text-xs text-muted hover:text-text"
                title={t.boardSettings}
              >
                ⚙
              </button>
            ) : null}
            {ctx && !share && session ? (
              <Bell
                onClick={() => (setActivityOpen((v) => !v), setTrashOpen(false), setUnsortedOpen(false))}
              />
            ) : null}
            {ctx && !share ? (
              <UnsortedCount
                ctx={ctx}
                onClick={() => (setUnsortedOpen((v) => !v), setTrashOpen(false), setActivityOpen(false))}
              />
            ) : null}
            {ctx?.canEdit && !share ? (
              <button
                type="button"
                onClick={() => setGallery({})}
                className="text-muted hover:text-text"
                title={t.templates}
              >
                ▦
              </button>
            ) : null}
            {ctx && !share ? (
              <button
                type="button"
                onClick={() => (
                  setVersionsOpen((v) => !v),
                  setTrashOpen(false),
                  setUnsortedOpen(false),
                  setActivityOpen(false)
                )}
                className="text-muted hover:text-text"
                title={t.history}
              >
                🕒
              </button>
            ) : null}
            {ctx?.canEdit && !share ? (
              <button
                type="button"
                onClick={() => (setTrashOpen((v) => !v), setUnsortedOpen(false), setVersionsOpen(false))}
                className="text-muted hover:text-text"
                title={t.trash}
              >
                🗑
              </button>
            ) : null}
            {me?.staffRole ? (
              <>
                <Link to="/equipo" className="text-muted hover:text-text">
                  {t.team}
                </Link>
                <Link to="/papelera" className="text-muted hover:text-text">
                  {t.trash}
                </Link>
              </>
            ) : null}
            {share ? (
              <a href="/" className="text-[11px] text-dim hover:text-text">
                {t.madeWith} <span className="wordmark">.MAYUSCULANOTE</span>
              </a>
            ) : null}
            {session && !share ? (
              <Link to="/guia" className="text-muted hover:text-text" title={t.guide}>
                {t.guide}
              </Link>
            ) : null}
            {session && !share ? (
              <FeedbackButton boardId={view?.board.id ?? null} className="hidden md:inline" />
            ) : null}
            {session && !share ? (
              <Link to="/ajustes" className="text-muted hover:text-text" title={t.settings}>
                {t.settings}
              </Link>
            ) : null}
            <LanguageToggle />
          </div>
          <MobileMenu
            items={[
              ...(ctx && can(ctx.role, "manage") && view?.board.parentId
                ? [{ label: t.inviteGuest, run: () => setSharing(true) }]
                : []),
              ...(ctx ? [{ label: t.present, run: () => setPresenting(true) }] : []),
              ...(ctx?.canEdit && view && !share
                ? [{ label: t.boardSettings, run: () => setSettingsFor(view.board) }]
                : []),
              ...(ctx?.canEdit && !share ? [{ label: t.templates, run: () => setGallery({}) }] : []),
              ...(ctx && !share
                ? [
                    {
                      label: t.history,
                      run: () => (
                        setVersionsOpen(true),
                        setTrashOpen(false),
                        setUnsortedOpen(false),
                        setActivityOpen(false)
                      ),
                    },
                    {
                      label: t.activity,
                      run: () => (
                        setActivityOpen(true),
                        setTrashOpen(false),
                        setUnsortedOpen(false),
                        setVersionsOpen(false)
                      ),
                    },
                    {
                      label: t.unsorted,
                      run: () => (
                        setUnsortedOpen(true),
                        setTrashOpen(false),
                        setActivityOpen(false),
                        setVersionsOpen(false)
                      ),
                    },
                  ]
                : []),
              ...(ctx?.canEdit && !share
                ? [
                    {
                      label: t.trash,
                      run: () => (
                        setTrashOpen(true),
                        setUnsortedOpen(false),
                        setVersionsOpen(false),
                        setActivityOpen(false)
                      ),
                    },
                  ]
                : []),
              ...(view
                ? ["pdf", "pdf-light", "png", "docx", "md", "zip"].map((f) => ({
                    label: `${t.exportBoard}: ${t.exportFormats[f]}`,
                    href: `/api/boards/${view.board.id}/export/${f.replace("-light", "")}${f.endsWith("-light") ? "?theme=light" : ""}`,
                  }))
                : []),
              ...(me?.staffRole
                ? [
                    { label: t.team, href: "/equipo" },
                    { label: t.trash, href: "/papelera" },
                  ]
                : []),
              ...(session && !share
                ? [
                    { label: t.guide, href: "/guia" },
                    { label: t.settings, href: "/ajustes" },
                  ]
                : []),
              ...(session && !share && me?.workspace.feedbackEnabled
                ? [{ label: t.feedback.button, run: () => openFeedback(view?.board.id ?? null) }]
                : []),
              { label: t.shortcutsTitle, run: () => setHelpOpen(true) },
            ]}
          />
        </div>
      </header>
      <div className="relative min-h-0 flex-1" ref={viewportHostRef}>
        {ctx && actions ? (
          <BoardContext.Provider value={ctx}>
            <Canvas
              onCreateAt={(x, y) => void actions.create("note", x, y)}
              background={view?.board.background}
            />
            {ctx.canEdit && !presenting ? (
              <Toolbar
                onPick={createAtCenter}
                onConnect={() => actions.connect()}
                onUpload={(kind) => {
                  setUploadAccept(
                    kind === "image"
                      ? "image/*"
                      : kind === "video"
                        ? "video/*"
                        : kind === "audio"
                          ? "audio/*"
                          : "*/*",
                  );
                  setTimeout(() => fileInput.current?.click(), 0);
                }}
              />
            ) : null}
            <ContextMenu actions={actions} canEdit={ctx.canEdit} />
            <Pickers actions={actions} canEdit={ctx.canEdit} />
            {trashOpen ? (
              <TrashPanel boardId={ctx.boardId} actions={actions} onClose={() => setTrashOpen(false)} />
            ) : null}
            {unsortedOpen ? <UnsortedPanel actions={actions} onClose={() => setUnsortedOpen(false)} /> : null}
            {quickOpen ? <QuickNotesPanel target={ctx} onClose={() => setQuickOpen(false)} /> : null}
            {activityOpen ? (
              <ActivityPanel boardId={ctx.boardId} onClose={() => setActivityOpen(false)} />
            ) : null}
            {versionsOpen ? (
              <VersionsPanel
                boardId={ctx.boardId}
                canEdit={ctx.canEdit}
                onClose={() => setVersionsOpen(false)}
              />
            ) : null}
            {presenting ? (
              <Presentation onExit={() => setPresenting(false)} />
            ) : (
              <>
                <ZoomControls onFit={zoomFit} />
                {ctx.canEdit ? <SelectionBar actions={actions} /> : null}
              </>
            )}
            <EmptyState ctx={ctx} synced={synced} />
            {offerTemplates ? (
              <div className="absolute right-3 z-20 flex items-center gap-2 rounded-md border border-border bg-panel p-3 text-sm shadow-panel max-md:bottom-28 md:bottom-16">
                <Button className="px-3 py-1.5 text-xs" onClick={() => setGallery({ into: ctx.boardId })}>
                  {t.chooseTemplate}
                </Button>
                <button
                  type="button"
                  className="text-dim hover:text-text"
                  onClick={() => (
                    sessionStorage.setItem(`mn_tpl_offered_${ctx.boardId}`, "1"), setOfferTemplates(false)
                  )}
                >
                  ✕
                </button>
              </div>
            ) : null}
            {gallery ? (
              <TemplateGallery
                intoBoardId={gallery.into}
                parentBoardId={ctx.boardId}
                onClose={() => setGallery(null)}
                onApplied={(bid) => {
                  setGallery(null);
                  setOfferTemplates(false);
                  if (bid !== ctx.boardId) nav(`/b/${bid}`);
                }}
              />
            ) : null}
          </BoardContext.Provider>
        ) : (
          <div className="flex items-center gap-2 p-8 text-muted">
            <Spinner /> {t.loading}
          </div>
        )}
        <input
          ref={fileInput}
          type="file"
          accept={uploadAccept}
          multiple
          hidden
          onChange={(e) => (void addImages([...(e.target.files ?? [])]), (e.target.value = ""))}
        />
      </div>
      {sharing && view ? <ShareDialog boardId={view.board.id} onClose={() => setSharing(false)} /> : null}
      {searchOpen ? <SearchDialog onClose={() => setSearchOpen(false)} /> : null}
      {helpOpen ? <ShortcutsDialog onClose={() => setHelpOpen(false)} /> : null}
      {settingsFor ? (
        <BoardSettingsDialog
          board={settingsFor}
          canSetPrivate={ctx?.role === "owner" && !!settingsFor.parentId}
          privateVia={settingsFor.id === view?.board.id ? (view?.privateVia ?? null) : null}
          onClose={() => setSettingsFor(null)}
          onSaved={(b) => {
            if (view && b.id === view.board.id)
              setView({
                ...view,
                board: b,
                breadcrumbs: view.breadcrumbs.map((c) => (c.id === b.id ? { ...c, title: b.title } : c)),
                privateVia: b.privateOwnerId
                  ? { id: b.id, title: b.title, ownerName: me?.user.name ?? null }
                  : view.privateVia && view.privateVia.id !== b.id
                    ? view.privateVia
                    : null,
              });
          }}
        />
      ) : null}
    </div>
  );
}

function ZoomControls({ onFit }: { onFit: () => void }) {
  const z = useBoardUI((s) => s.camera.z);
  const setCamera = useBoardUI((s) => s.setCamera);
  const step = (f: number) =>
    setCamera((c) => {
      const { w, h } = useBoardUI.getState().viewport;
      return zoomAt(c, w / 2, h / 2, c.z * f);
    });
  return (
    <div className="absolute right-3 z-20 flex items-center gap-1 rounded-md border border-border bg-panel p-1 text-xs shadow-panel max-md:bottom-16 md:bottom-3">
      <button type="button" onClick={() => step(1 / 1.2)} className="size-7 rounded-sm hover:bg-panel-2">
        −
      </button>
      <button type="button" onClick={onFit} className="min-w-12 rounded-sm px-1 hover:bg-panel-2" title="Z">
        {Math.round(z * 100)}%
      </button>
      <button type="button" onClick={() => step(1.2)} className="size-7 rounded-sm hover:bg-panel-2">
        +
      </button>
    </div>
  );
}

/** Header button with the count of unsorted cards. */
function UnsortedCount({ ctx, onClick }: { ctx: BoardCtx; onClick: () => void }) {
  const t = useT();
  const n = useFlaggedCards(ctx.handle.types.cards, "unsorted").length;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-muted hover:text-text"
      title={t.unsorted}
    >
      ▤
      {n > 0 ? (
        <span className="rounded-full bg-[#f4511c] px-1.5 text-[10px] font-bold text-white">{n}</span>
      ) : null}
    </button>
  );
}

/** Header pill: who can see this board. Inicio is always the team's; others toggle in board settings. */
function VisibilityPill({ view, onClick }: { view: BoardView; onClick?: () => void }) {
  const t = useT();
  const root = !view.board.parentId;
  const priv = !!view.privateVia;
  const label = root ? t.visibility.rootPill : priv ? t.visibility.privatePill : t.visibility.teamPill;
  const title = root
    ? t.visibility.rootHint
    : priv
      ? view.privateVia!.id === view.board.id
        ? t.visibility.privateHint
        : t.visibility.via(view.privateVia!.title, view.privateVia!.ownerName)
      : t.visibility.teamHint;
  const cls = `shrink-0 rounded-full border px-2 py-0.5 text-[11px] leading-4 ${
    priv ? "border-accent/50 text-text" : "border-border text-dim"
  } ${onClick ? "hover:text-text" : ""}`;
  const content = (
    <>
      {priv ? "🔒 " : root ? "👥 " : ""}
      {label}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className={cls} title={title}>
      {content}
    </button>
  ) : (
    <span className={cls} title={title}>
      {content}
    </span>
  );
}

/** Compact "⋯" menu for small screens holding what the desktop header shows inline. */
function MobileMenu({ items }: { items: { label: string; run?: () => void; href?: string }[] }) {
  const [open, setOpen] = useState(false);
  const { signOut } = useSession();
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [open]);
  if (items.length === 0) return null;
  return (
    <div ref={ref} className="relative md:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-sm border border-border px-2 py-1 text-base leading-none text-muted"
        aria-label="Menu"
      >
        ⋯
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-40 mt-1 max-h-[70vh] min-w-56 overflow-y-auto rounded-md border border-border bg-panel py-1 shadow-panel">
          {items.map((it) =>
            it.href ? (
              <a
                key={it.label}
                href={it.href}
                download={it.href.includes("/export/") ? true : undefined}
                onClick={() => setOpen(false)}
                className="block px-3 py-2 text-sm text-text no-underline hover:bg-panel-2"
              >
                {it.label}
              </a>
            ) : (
              <button
                key={it.label}
                type="button"
                onClick={() => (setOpen(false), it.run?.())}
                className="block w-full px-3 py-2 text-left text-sm text-text hover:bg-panel-2"
              >
                {it.label}
              </button>
            ),
          )}
          <div className="my-1 border-t border-border" />
          <div className="px-3 py-2">
            <LanguageToggle />
          </div>
          <div className="px-3 py-1 text-[11px] text-dim">{__APP_VERSION__}</div>
          <button
            type="button"
            onClick={signOut}
            className="block w-full px-3 py-2 text-left text-sm text-muted hover:bg-panel-2"
          >
            {t.signOut}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Touch devices have no right-click: show the key card actions when something is selected. */
function SelectionBar({ actions }: { actions: Actions }) {
  const selection = useBoardUI((s) => s.selection);
  const t = useT();
  const [coarse] = useState(() => window.matchMedia?.("(pointer: coarse)").matches ?? false);
  if (!coarse || selection.size === 0) return null;
  const first = [...selection][0]!;
  const more = () => {
    const vp = useBoardUI.getState().viewport;
    useBoardUI
      .getState()
      .setContextMenu({ sx: 12, sy: Math.max(12, vp.h - 420), px: 0, py: 0, cardId: first });
  };
  const btn = "rounded-sm px-3 py-1.5 text-sm hover:bg-panel-2";
  return (
    <div className="absolute left-3 z-20 flex items-center gap-1 rounded-md border border-border bg-panel p-1 shadow-panel max-md:bottom-16 md:bottom-3">
      <button type="button" className={btn} onClick={more}>
        {t.menu.labels?.replace("…", "") ?? "Labels"} / ⋯
      </button>
      <button type="button" className={btn} onClick={() => actions.duplicate()}>
        {t.menu.duplicate}
      </button>
      <button type="button" className={`${btn} text-error`} onClick={() => actions.delete()}>
        {t.moveToTrash}
      </button>
    </div>
  );
}

/** Centered hint on a board with no cards: how to start (editors) or that nothing is here yet (viewers). */
function EmptyState({ ctx, synced }: { ctx: BoardCtx; synced: boolean }) {
  const ids = useMapKeys(ctx.handle.types.cards);
  const t = useT();
  if (!synced || ids.length > 0) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="display mb-2 text-3xl text-text">.{t.emptyBoard}</div>
        <p className="text-sm text-muted">{ctx.canEdit ? t.emptyBoardHow : t.emptyBoardViewer}</p>
      </div>
    </div>
  );
}
